import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

interface SoldListing {
  street_address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
  price: string | null;
  sold_date: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  living_area: number | null;
  represented: string | null;
  image_url: string | null;
}

interface SpotlightMapProps {
  soldListings: SoldListing[];
  primaryColor?: string | null;
}

export function SpotlightMap({ soldListings, primaryColor }: SpotlightMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapboxToken, setMapboxToken] = useState("");
  const [webglError, setWebglError] = useState(false);
  const queryClient = useQueryClient();

  const markerColor = primaryColor ?? "#3B82F6";

  // Filter to only listings with valid, non-zero coordinates — memoized to stabilize effect deps
  const mappableListings = useMemo(
    () =>
      soldListings.filter(
        (l) =>
          typeof l.latitude === "number" &&
          typeof l.longitude === "number" &&
          (l.latitude !== 0 || l.longitude !== 0)
      ),
    [soldListings]
  );

  // Fetch Mapbox token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const cachedToken = queryClient.getQueryData<string>(["mapbox-token", "public"]);
        if (cachedToken) {
          setMapboxToken(cachedToken);
          return;
        }
        const { data, error } = await supabase.functions.invoke("get-mapbox-token", {
          body: { demo: true },
        });
        if (!error && data?.token) {
          setMapboxToken(data.token);
          queryClient.setQueryData(["mapbox-token", "public"], data.token);
        }
      } catch (error) {
        logger.error("Error fetching Mapbox token:", error);
      }
    };
    fetchToken();
  }, [queryClient]);

  // Initialize map and place markers using lat/lng from sold listings
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || mappableListings.length === 0) return;

    try {
      mapboxgl.accessToken = mapboxToken;
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [-98.5, 39.8],
        zoom: 3,
        interactive: false,
        failIfMajorPerformanceCaveat: false,
      });

      map.current.on("load", () => {
        const bounds = new mapboxgl.LngLatBounds();

        for (const listing of mappableListings) {
          const lng = listing.longitude!;
          const lat = listing.latitude!;
          bounds.extend([lng, lat]);

          const el = document.createElement("div");
          el.style.width = "14px";
          el.style.height = "14px";
          el.style.backgroundColor = markerColor;
          el.style.borderRadius = "50%";
          el.style.border = "2px solid white";
          el.style.boxShadow = `0 0 12px ${markerColor}80, 0 1px 4px rgba(0,0,0,0.3)`;

          const location = [listing.city, listing.state].filter(Boolean).join(", ");
          const details = [
            listing.bedrooms ? `${listing.bedrooms} bd` : null,
            listing.bathrooms ? `${listing.bathrooms} ba` : null,
            listing.living_area ? `${listing.living_area.toLocaleString()} sqft` : null,
          ].filter(Boolean).join(" · ");

          const popup = new mapboxgl.Popup({ offset: 15, maxWidth: "220px" }).setHTML(`
            <div style="font-family: system-ui, sans-serif;">
              ${listing.image_url ? `<img src="${listing.image_url}" style="width:100%;height:90px;object-fit:cover;border-radius:4px;margin-bottom:6px;" />` : ""}
              <p style="font-weight:600;font-size:13px;margin:0 0 2px;">${listing.street_address}</p>
              ${location ? `<p style="color:#666;font-size:12px;margin:0 0 4px;">${location}</p>` : ""}
              ${listing.price ? `<p style="font-weight:700;font-size:14px;margin:0 0 2px;">${listing.price}</p>` : ""}
              ${listing.sold_date ? `<p style="color:#888;font-size:11px;margin:0 0 2px;">Sold ${listing.sold_date}</p>` : ""}
              ${details ? `<p style="color:#888;font-size:11px;margin:0;">${details}</p>` : ""}
            </div>
          `);

          new mapboxgl.Marker(el)
            .setLngLat([lng, lat])
            .setPopup(popup)
            .addTo(map.current!);
        }

        if (!bounds.isEmpty()) {
          map.current?.fitBounds(bounds, { padding: 60, maxZoom: 11 });
        }
      });
    } catch (error) {
      logger.error("Error initializing Spotlight map:", error);
      setWebglError(true);
    }

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [mapboxToken, mappableListings, markerColor]);

  if (mappableListings.length === 0) return null;
  if (webglError) return null;
  if (!mapboxToken) return null;

  return (
    <section className="relative w-full">
      {/* Floating glass label */}
      <div className="absolute top-8 left-8 z-20 backdrop-blur-md bg-white/10 border border-white/20 rounded-xl px-4 py-2">
        <p className="text-white text-sm font-medium">Our Coverage Area</p>
      </div>

      <div ref={mapContainer} className="w-full h-[500px] md:h-[600px] lg:h-[70vh]" />
    </section>
  );
}
